import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSend, FiBell, FiCalendar, FiUsers, FiSettings, FiChevronDown, FiArrowUp, FiArrowDown, FiCheck } from 'react-icons/fi';
import { IconType } from 'react-icons';

interface NotificationForm {
  // Bildirim Gönder
  notificationType: 'in-app' | 'out-app';
  sendMode: 'full-auto' | 'semi-auto' | 'manual';
  
  // Gönderim Sıklığı
  frequencyAuto: boolean;
  frequencyDays: number;
  
  // Nasıl Belirle
  categories: string[];
  userFilters: {
    age: string;
    country: string;
    target: string;
    sendDate: string;
  };
  personalMessage: boolean;
  
  // Bildirim Detayları
  title: string;
  content: string;
  note: string;
  imageUrl: string;
  
  // Kime Gönder
  city: string;
  ageRange: string;
  lastLogin: string;
  gender: string;
  lastLoginDays: number;
  
  // Ne Zaman Gönder
  scheduleType: 'immediate' | 'scheduled';
  scheduledDate: string;
  scheduledTime: string;
}

interface PastNotification {
  id: number;
  date: string;
  title: string;
  note: string;
  sentCount: number;
}

const NotificationCenter: React.FC = () => {
  const renderIcon = (Icon: IconType, className: string) => {
    const IconComponent = Icon as React.ComponentType<{ className?: string }>;
    return <IconComponent className={className} />;
  };

  const [formData, setFormData] = useState<NotificationForm>({
    notificationType: 'in-app',
    sendMode: 'full-auto',
    frequencyAuto: true,
    frequencyDays: 1,
    categories: [],
    userFilters: {
      age: '',
      country: '',
      target: '',
      sendDate: '',
    },
    personalMessage: false,
    title: '',
    content: '',
    note: '',
    imageUrl: '',
    city: 'all',
    ageRange: 'all',
    lastLogin: 'all',
    gender: 'all',
    lastLoginDays: 1,
    scheduleType: 'immediate',
    scheduledDate: '',
    scheduledTime: '',
  });

  const [pastNotifications, setPastNotifications] = useState<PastNotification[]>([
    // Örnek veriler
    { id: 1, date: '2024-01-15', title: 'Yeni Test Duyurusu', note: 'Test bildirimi', sentCount: 1250 },
    { id: 2, date: '2024-01-14', title: 'Oyun Güncellemesi', note: 'Oyun bildirimi', sentCount: 2100 },
  ]);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const categories = ['Test', 'Oyun', 'Chat', 'Danger', 'Motivasyon', 'Content'];
  const cities = ['Hepsi', 'İstanbul', 'Ankara', 'İzmir', 'Bursa'];
  const ageRanges = ['Hepsi', '18-25', '26-35', '36-45', '46+'];
  const lastLoginOptions = ['Hepsi', 'Son 1 gün', 'Son 2 gün', 'Son 3 gün', 'Son 7 gün'];
  const genders = ['Hepsi', 'Erkek', 'Kadın', 'Belirtilmemiş'];

  const handleInputChange = (field: keyof NotificationForm, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleUserFilterChange = (field: keyof NotificationForm['userFilters'], value: string) => {
    setFormData(prev => ({
      ...prev,
      userFilters: { ...prev.userFilters, [field]: value }
    }));
  };

  const toggleCategory = (category: string) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Bildirim gönderiliyor:', formData);
    // API çağrısı burada yapılacak
    alert('Bildirim gönderildi!');
  };

  // Handle sort
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Sort notifications
  const sortedNotifications = useMemo(() => {
    if (!sortColumn) return pastNotifications;

    return [...pastNotifications].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'date':
          aValue = new Date(a.date).getTime();
          bValue = new Date(b.date).getTime();
          break;
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'note':
          aValue = a.note.toLowerCase();
          bValue = b.note.toLowerCase();
          break;
        case 'sentCount':
          aValue = a.sentCount;
          bValue = b.sentCount;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [pastNotifications, sortColumn, sortDirection]);

  return (
    <div className="w-full max-w-7xl mx-auto space-y-6 p-6">
      <motion.div 
        className="mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 dark:from-indigo-400 dark:via-purple-400 dark:to-pink-400 bg-clip-text text-transparent mb-2">
          Bildirim Merkezi
        </h1>
        <p className="text-slate-600 dark:text-slate-400">Kullanıcılara bildirim gönderin ve yönetin</p>
      </motion.div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Bildirim Gönder Bölümü */}
        <motion.div 
          className="glass-strong rounded-2xl shadow-xl p-6 border border-white/10 dark:border-gray-700/50 backdrop-blur-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
            {renderIcon(FiBell, "w-5 h-5 text-indigo-600 dark:text-indigo-400")}
            Bildirim Gönder
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <motion.div
              onClick={() => handleInputChange('notificationType', 'in-app')}
              className={`p-4 rounded-xl cursor-pointer transition-all relative overflow-hidden ${
                formData.notificationType === 'in-app'
                  ? 'bg-gradient-to-br from-indigo-500/20 via-purple-500/20 to-pink-500/20 dark:from-indigo-500/10 dark:via-purple-500/10 dark:to-pink-500/10 border-2 border-indigo-500/50 shadow-lg'
                  : 'bg-white/50 dark:bg-gray-800/50 border-2 border-gray-200/50 dark:border-gray-700/50 hover:border-indigo-300/50 dark:hover:border-indigo-600/50'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {formData.notificationType === 'in-app' && (
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-indigo-400/10 via-purple-400/10 to-pink-400/10"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />
              )}
              <div className="relative z-10">
                <div className="font-semibold text-slate-800 dark:text-white mb-1">Uygulama İçi</div>
                <div className="text-sm text-slate-600 dark:text-slate-400">Tam Otomatik Açık</div>
              </div>
            </motion.div>
            
            <motion.div
              onClick={() => handleInputChange('notificationType', 'out-app')}
              className={`p-4 rounded-xl cursor-pointer transition-all relative overflow-hidden ${
                formData.notificationType === 'out-app'
                  ? 'bg-gradient-to-br from-indigo-500/20 via-purple-500/20 to-pink-500/20 dark:from-indigo-500/10 dark:via-purple-500/10 dark:to-pink-500/10 border-2 border-indigo-500/50 shadow-lg'
                  : 'bg-white/50 dark:bg-gray-800/50 border-2 border-gray-200/50 dark:border-gray-700/50 hover:border-indigo-300/50 dark:hover:border-indigo-600/50'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {formData.notificationType === 'out-app' && (
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-indigo-400/10 via-purple-400/10 to-pink-400/10"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />
              )}
              <div className="relative z-10">
                <div className="font-semibold text-slate-800 dark:text-white mb-1">Uygulama Dışı</div>
                <div className="text-sm text-slate-600 dark:text-slate-400">Tam Otomatik Kapalı</div>
              </div>
            </motion.div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
              Gönderim Modu
            </label>
            <div className="space-y-2">
              {[
                { value: 'full-auto', label: 'Tam Otomatik' },
                { value: 'semi-auto', label: 'Yarı Otomatik' },
                { value: 'manual', label: 'Manuel Gönderme' },
              ].map(option => {
                const isSelected = formData.sendMode === option.value;
                return (
                  <motion.label 
                    key={option.value} 
                    className={`flex items-center gap-3 cursor-pointer group p-3 rounded-xl transition-all ${
                      isSelected 
                        ? 'bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 dark:from-indigo-500/5 dark:via-purple-500/5 dark:to-pink-500/5 border-2 border-indigo-500/30 shadow-md' 
                        : 'bg-white/30 dark:bg-gray-800/30 border-2 border-transparent hover:border-indigo-200/50 dark:hover:border-indigo-800/50'
                    }`}
                    whileHover={{ scale: 1.01, x: 4 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <div className="relative flex items-center justify-center">
                      <input
                        type="radio"
                        name="sendMode"
                        value={option.value}
                        checked={isSelected}
                        onChange={(e) => handleInputChange('sendMode', e.target.value as any)}
                        className="sr-only"
                      />
                      <motion.div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          isSelected 
                            ? 'border-indigo-600 dark:border-indigo-400 bg-indigo-600 dark:bg-indigo-400' 
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                        }`}
                        animate={{
                          scale: isSelected ? [1, 1.2, 1] : 1,
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                          >
                            {renderIcon(FiCheck, "w-3 h-3 text-white")}
                          </motion.div>
                        )}
                      </motion.div>
                    </div>
                    <span className={`text-sm font-medium flex-1 ${isSelected ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>
                      {option.label}
                    </span>
                    {isSelected && (
                      <motion.div 
                        className="flex items-center gap-1"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="w-0 h-0 border-l-[8px] border-l-yellow-400 border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent"></div>
                        <div className="text-xs text-yellow-600 dark:text-yellow-400 font-semibold">→</div>
                      </motion.div>
                    )}
                  </motion.label>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Gönderim Sıklığı - Sadece Tam Otomatik'te göster */}
        {formData.sendMode === 'full-auto' && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
              {renderIcon(FiSettings, "w-5 h-5 text-indigo-600 dark:text-indigo-400")}
              Gönderim Sıklığı
            </h2>
            
            <div className="space-y-4">
              <motion.label 
                className="flex items-center gap-3 cursor-pointer p-3 rounded-xl bg-white/30 dark:bg-gray-800/30 hover:bg-white/50 dark:hover:bg-gray-800/50 transition-colors"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <input
                  type="checkbox"
                  checked={formData.frequencyAuto}
                  onChange={(e) => handleInputChange('frequencyAuto', e.target.checked)}
                  className="sr-only"
                />
                <motion.div
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    formData.frequencyAuto 
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600' 
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                  animate={{
                    backgroundColor: formData.frequencyAuto 
                      ? ['#4f46e5', '#9333ea', '#4f46e5']
                      : '#d1d5db'
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div
                    className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-lg flex items-center justify-center"
                    animate={{
                      x: formData.frequencyAuto ? 24 : 0,
                    }}
                    transition={{
                      type: 'spring',
                      stiffness: 500,
                      damping: 30,
                    }}
                  >
                    {formData.frequencyAuto && (
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                      >
                        {renderIcon(FiCheck, "w-3 h-3 text-indigo-600")}
                      </motion.div>
                    )}
                  </motion.div>
                </motion.div>
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Otomatik</span>
              </motion.label>

              <div className="flex gap-3">
                {[1, 2].map((days) => {
                  const isSelected = formData.frequencyDays === days;
                  return (
                    <motion.label 
                      key={days}
                      className={`flex items-center gap-2 cursor-pointer px-4 py-2 rounded-lg transition-all ${
                        isSelected
                          ? 'bg-gradient-to-r from-indigo-500/20 to-purple-500/20 dark:from-indigo-500/10 dark:to-purple-500/10 border-2 border-indigo-500/50 shadow-md'
                          : 'bg-white/30 dark:bg-gray-800/30 border-2 border-transparent hover:border-indigo-200/50 dark:hover:border-indigo-800/50'
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <input
                        type="radio"
                        name="frequencyDays"
                        value={days}
                        checked={isSelected}
                        onChange={(e) => handleInputChange('frequencyDays', parseInt(e.target.value))}
                        className="sr-only"
                      />
                      <motion.div
                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          isSelected 
                            ? 'border-indigo-600 dark:border-indigo-400 bg-indigo-600 dark:bg-indigo-400' 
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                        }`}
                        animate={{
                          scale: isSelected ? [1, 1.2, 1] : 1,
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                            className="w-2 h-2 bg-white rounded-full"
                          />
                        )}
                      </motion.div>
                      <span className={`text-sm font-medium ${isSelected ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>
                        {days} Gün
                      </span>
                    </motion.label>
                  );
                })}
              </div>

              {/* Geçmiş ve Gönderimler Grupları */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                {/* Sol Grup - Geçmiş */}
                <div className="relative">
                  <div className="absolute -left-4 top-0 w-0.5 h-full bg-indigo-200 dark:bg-indigo-800"></div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                    Geçmiş
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 rounded"
                      />
                      <div className="flex-1 h-8 bg-gray-100 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600"></div>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 rounded"
                      />
                      <div className="flex-1 h-8 bg-gray-100 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600"></div>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 rounded"
                      />
                      <div className="flex-1 h-8 bg-gray-100 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600"></div>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 rounded"
                      />
                      <div className="flex-1 h-8 bg-gray-100 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600"></div>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 rounded"
                      />
                      <div className="flex-1 h-8 bg-gray-100 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600"></div>
                    </label>
                  </div>
                </div>

                {/* Sağ Grup - Gönderimler */}
                <div className="relative">
                  <div className="absolute -right-4 top-0 flex items-center">
                    <div className="w-0 h-0 border-l-[12px] border-l-yellow-400 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent"></div>
                    <span className="text-xs font-semibold text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 px-2 py-1 rounded ml-1">
                      →
                    </span>
                  </div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                    Gönderimler
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 rounded"
                      />
                      <div className="flex-1 h-8 bg-gray-100 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600"></div>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 rounded"
                      />
                      <div className="flex-1 h-8 bg-gray-100 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600"></div>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 rounded"
                      />
                      <div className="flex-1 h-8 bg-gray-100 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600"></div>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 rounded"
                      />
                      <div className="flex-1 h-8 bg-gray-100 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600"></div>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 rounded"
                      />
                      <div className="flex-1 h-8 bg-gray-100 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Nasıl Belirle ve Kullanıcı - Sadece Yarı Otomatik ve Manuel'de göster */}
        {(formData.sendMode === 'semi-auto' || formData.sendMode === 'manual') && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 border-2 border-yellow-400 dark:border-yellow-500 border-dashed relative">
            {/* Sarı ok gösterimi */}
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 flex items-center gap-2">
              <div className="w-0 h-0 border-l-[12px] border-l-yellow-400 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent"></div>
              <span className="text-xs font-semibold text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 px-2 py-1 rounded">
                Gönderim Modu → Nasıl Belirle
              </span>
            </div>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
              {renderIcon(FiUsers, "w-5 h-5 text-indigo-600 dark:text-indigo-400")}
              Nasıl Belirle ve Kullanıcı
            </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                Kategori Seçimi
              </label>
              <div className="space-y-2">
                {categories.map(category => {
                  const isSelected = formData.categories.includes(category);
                  return (
                    <motion.label 
                      key={category} 
                      className={`flex items-center gap-3 cursor-pointer p-2.5 rounded-lg transition-all ${
                        isSelected
                          ? 'bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 border-2 border-indigo-500/30'
                          : 'bg-white/30 dark:bg-gray-800/30 border-2 border-transparent hover:border-indigo-200/50 dark:hover:border-indigo-800/50'
                      }`}
                      whileHover={{ scale: 1.02, x: 4 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleCategory(category)}
                        className="sr-only"
                      />
                      <motion.div
                        className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                          isSelected 
                            ? 'bg-gradient-to-br from-indigo-600 to-purple-600 border-indigo-600' 
                            : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                        }`}
                        animate={{
                          scale: isSelected ? [1, 1.1, 1] : 1,
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0, rotate: -180 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                          >
                            {renderIcon(FiCheck, "w-3 h-3 text-white")}
                          </motion.div>
                        )}
                      </motion.div>
                      <span className={`text-sm font-medium flex-1 ${isSelected ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>
                        {category}
                      </span>
                    </motion.label>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                Kullanıcı Filtreleri
              </label>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-2">Yaş</label>
                  <motion.input
                    type="text"
                    value={formData.userFilters.age}
                    onChange={(e) => handleUserFilterChange('age', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200/50 dark:border-gray-700/50 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500"
                    placeholder="Yaş aralığı"
                    whileFocus={{ scale: 1.01 }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-2">Ülke</label>
                  <motion.input
                    type="text"
                    value={formData.userFilters.country}
                    onChange={(e) => handleUserFilterChange('country', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200/50 dark:border-gray-700/50 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500"
                    placeholder="Ülke kodu"
                    whileFocus={{ scale: 1.01 }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-2">Hedef</label>
                  <motion.input
                    type="text"
                    value={formData.userFilters.target}
                    onChange={(e) => handleUserFilterChange('target', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200/50 dark:border-gray-700/50 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500"
                    placeholder="Hedef kitle"
                    whileFocus={{ scale: 1.01 }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-2">Gönderim Tarihi</label>
                  <motion.input
                    type="date"
                    value={formData.userFilters.sendDate}
                    onChange={(e) => handleUserFilterChange('sendDate', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200/50 dark:border-gray-700/50 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all"
                    whileFocus={{ scale: 1.01 }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <motion.label 
              className="flex items-center gap-3 cursor-pointer p-3 rounded-xl bg-white/30 dark:bg-gray-800/30 hover:bg-white/50 dark:hover:bg-gray-800/50 transition-colors"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              <input
                type="checkbox"
                checked={formData.personalMessage}
                onChange={(e) => handleInputChange('personalMessage', e.target.checked)}
                className="sr-only"
              />
              <motion.div
                className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                  formData.personalMessage 
                    ? 'bg-gradient-to-br from-indigo-600 to-purple-600 border-indigo-600' 
                    : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                }`}
                animate={{
                  scale: formData.personalMessage ? [1, 1.1, 1] : 1,
                }}
                transition={{ duration: 0.3 }}
              >
                {formData.personalMessage && (
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                  >
                    {renderIcon(FiCheck, "w-3 h-3 text-white")}
                  </motion.div>
                )}
              </motion.div>
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Kişiye Özel Gerekli</span>
            </motion.label>
          </div>

          {/* Otomatik Mesaj Akışı ve Bireysel Özel/Genel - Sadece Yarı Otomatik'te */}
          {formData.sendMode === 'semi-auto' && (
            <>
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="md:col-span-2">
                    <button
                      type="button"
                      className="w-full px-4 py-3 text-left bg-indigo-50 dark:bg-indigo-900/20 border-2 border-indigo-200 dark:border-indigo-800 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
                    >
                      <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">Otomatik Mesaj Akışı</span>
                    </button>
                  </div>
                  <div>
                    <button
                      type="button"
                      className="w-full px-4 py-2 text-left bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    >
                      <span className="text-xs font-medium text-slate-700 dark:text-slate-300">Bireysel Özel/Genel</span>
                    </button>
                    <div className="mt-2 flex gap-2">
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="radio"
                          name="individualType"
                          value="yes"
                          className="w-3 h-3 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-xs text-slate-600 dark:text-slate-400">Evet</span>
                      </label>
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="radio"
                          name="individualType"
                          value="no"
                          className="w-3 h-3 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-xs text-slate-600 dark:text-slate-400">Hayır</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Checkbox'lar ve Input Alanları */}
                <div className="space-y-3 mt-4">
                  {[1, 2, 3, 4].map((index) => {
                    const defaultChecked = index <= 3;
                    return (
                      <motion.div 
                        key={index} 
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/30 dark:hover:bg-gray-800/30 transition-colors"
                        whileHover={{ scale: 1.01 }}
                      >
                        <motion.label className="cursor-pointer">
                          <input
                            type="checkbox"
                            defaultChecked={defaultChecked}
                            className="sr-only"
                          />
                          <motion.div
                            className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                              defaultChecked 
                                ? 'bg-gradient-to-br from-indigo-600 to-purple-600 border-indigo-600' 
                                : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                            }`}
                          >
                            {defaultChecked && (
                              renderIcon(FiCheck, "w-3 h-3 text-white")
                            )}
                          </motion.div>
                        </motion.label>
                        <motion.input
                          type="text"
                          className="flex-1 px-3 py-2 border-2 border-gray-200/50 dark:border-gray-700/50 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500"
                          placeholder={`Alan ${index}`}
                          whileFocus={{ scale: 1.01 }}
                        />
                      </motion.div>
                    );
                  })}
                </div>

                {/* Alt Kısım Etiketleri */}
                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex flex-wrap items-center gap-4 text-xs text-slate-600 dark:text-slate-400">
                    <span className="font-medium">Grup &</span>
                    <span>Gelişmiş Mesaj</span>
                    <span>Tüm</span>
                    <span>Başlık</span>
                    <span>Net</span>
                  </div>
                </div>
              </div>
            </>
          )}
          </div>
        )}

        {/* Bildirim Detayları - Sadece Yarı Otomatik ve Manuel'de göster */}
        {(formData.sendMode === 'semi-auto' || formData.sendMode === 'manual') && (
          <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 ${formData.sendMode === 'manual' ? 'border border-gray-100 dark:border-gray-700' : 'border-2 border-yellow-400 dark:border-yellow-500 border-dashed'} relative`}>
            {/* Sarı ok gösterimi - Sadece Yarı Otomatik'te */}
            {formData.sendMode === 'semi-auto' && (
              <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 flex items-center gap-2">
                <div className="w-0 h-0 border-l-[12px] border-l-yellow-400 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent"></div>
                <span className="text-xs font-semibold text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 px-2 py-1 rounded">
                  Gönderim Modu → Bildirim Detayları
                </span>
              </div>
            )}
            <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
              {renderIcon(FiBell, "w-5 h-5 text-indigo-600 dark:text-indigo-400")}
              Bildirim Detayları
            </h2>
          
          <div className="space-y-4">
            {formData.sendMode === 'manual' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Bildirim TSME
                </label>
                <motion.input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200/50 dark:border-gray-700/50 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500"
                  placeholder="Bildirim TSME girin"
                  whileFocus={{ scale: 1.01 }}
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Bildirim Başlığı
              </label>
              <motion.input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200/50 dark:border-gray-700/50 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500"
                placeholder="Bildirim başlığını girin"
                whileFocus={{ scale: 1.01 }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Bildirim İçeriği
              </label>
              <motion.textarea
                value={formData.content}
                onChange={(e) => handleInputChange('content', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border-2 border-gray-200/50 dark:border-gray-700/50 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500 resize-none"
                placeholder="Bildirim içeriğini girin"
                whileFocus={{ scale: 1.01 }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Bildirim Notu
              </label>
              <motion.input
                type="text"
                value={formData.note}
                onChange={(e) => handleInputChange('note', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200/50 dark:border-gray-700/50 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500"
                placeholder="Bildirim notu"
                whileFocus={{ scale: 1.01 }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {formData.sendMode === 'manual' ? 'Görsel Yükle' : 'Görsel Öğe (URL)'}
              </label>
              {formData.sendMode === 'manual' ? (
                <motion.label
                  className="block cursor-pointer"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <div className="w-full px-4 py-3 border-2 border-dashed border-gray-300/50 dark:border-gray-600/50 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-slate-700 dark:text-slate-300 hover:border-indigo-500/50 dark:hover:border-indigo-500/50 transition-all text-center">
                    <span className="text-sm font-medium">Dosya Seç veya Sürükle</span>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleInputChange('imageUrl', URL.createObjectURL(file));
                      }
                    }}
                    className="hidden"
                  />
                </motion.label>
              ) : (
                <motion.input
                  type="url"
                  value={formData.imageUrl}
                  onChange={(e) => handleInputChange('imageUrl', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200/50 dark:border-gray-700/50 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500"
                  placeholder="https://example.com/image.jpg"
                  whileFocus={{ scale: 1.01 }}
                />
              )}
            </div>
          </div>
          </div>
        )}

        {/* Kime Gönder - Sadece Manuel'de göster */}
        {formData.sendMode === 'manual' && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
            {renderIcon(FiUsers, "w-5 h-5 text-indigo-600 dark:text-indigo-400")}
            Kime Gönder?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Ülke Seçimi
              </label>
              <motion.select
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200/50 dark:border-gray-700/50 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all appearance-none cursor-pointer"
                whileFocus={{ scale: 1.01 }}
              >
                {cities.map(city => (
                  <option key={city} value={city.toLowerCase() === 'hepsi' ? 'all' : city}>
                    {city}
                  </option>
                ))}
              </motion.select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Yaş Seçim
              </label>
              <motion.select
                value={formData.ageRange}
                onChange={(e) => handleInputChange('ageRange', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200/50 dark:border-gray-700/50 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all appearance-none cursor-pointer"
                whileFocus={{ scale: 1.01 }}
              >
                {ageRanges.map(range => (
                  <option key={range} value={range.toLowerCase() === 'hepsi' ? 'all' : range}>
                    {range}
                  </option>
                ))}
              </motion.select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Son Giriş Yapan
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <motion.select
                  value={formData.lastLogin}
                  onChange={(e) => handleInputChange('lastLogin', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200/50 dark:border-gray-700/50 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all appearance-none cursor-pointer"
                  whileFocus={{ scale: 1.01 }}
                >
                  {lastLoginOptions.map(option => (
                    <option key={option} value={option.toLowerCase() === 'hepsi' ? 'all' : option}>
                      {option}
                    </option>
                  ))}
                </motion.select>
                <div className="flex flex-col gap-1">
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <input
                      type="radio"
                      name="lastLoginDays"
                      value={1}
                      checked={formData.lastLoginDays === 1}
                      onChange={(e) => handleInputChange('lastLoginDays', parseInt(e.target.value))}
                      className="w-3 h-3 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-xs text-slate-600 dark:text-slate-400">son 1 gün</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <input
                      type="radio"
                      name="lastLoginDays"
                      value={2}
                      checked={formData.lastLoginDays === 2}
                      onChange={(e) => handleInputChange('lastLoginDays', parseInt(e.target.value))}
                      className="w-3 h-3 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-xs text-slate-600 dark:text-slate-400">son 2 gün</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <input
                      type="radio"
                      name="lastLoginDays"
                      value={3}
                      checked={formData.lastLoginDays === 3}
                      onChange={(e) => handleInputChange('lastLoginDays', parseInt(e.target.value))}
                      className="w-3 h-3 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-xs text-slate-600 dark:text-slate-400">son 3 gün</span>
                  </label>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Cinsiyet
              </label>
              <motion.select
                value={formData.gender}
                onChange={(e) => handleInputChange('gender', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200/50 dark:border-gray-700/50 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all appearance-none cursor-pointer"
                whileFocus={{ scale: 1.01 }}
              >
                {genders.map(gender => (
                  <option key={gender} value={gender.toLowerCase() === 'hepsi' ? 'all' : gender}>
                    {gender}
                  </option>
                ))}
              </motion.select>
            </div>
          </div>
          </div>
        )}

        {/* Ne Zaman Gönder - Sadece Manuel'de göster */}
        {formData.sendMode === 'manual' && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
            {renderIcon(FiCalendar, "w-5 h-5 text-indigo-600 dark:text-indigo-400")}
            Ne Zaman Gönderilecek?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
              <div className="space-y-3">
                <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Ne zaman göndermek
                </div>
                <div className="flex gap-3">
                  {[
                    { value: 'immediate', label: 'Şimdi' },
                    { value: 'scheduled', label: 'Tarih Saat Belirle' },
                  ].map(option => {
                    const isSelected = formData.scheduleType === option.value;
                    return (
                      <motion.label 
                        key={option.value}
                        className={`flex items-center gap-2 cursor-pointer px-4 py-2 rounded-lg transition-all ${
                          isSelected
                            ? 'bg-gradient-to-r from-indigo-500/20 to-purple-500/20 dark:from-indigo-500/10 dark:to-purple-500/10 border-2 border-indigo-500/50 shadow-md'
                            : 'bg-white/30 dark:bg-gray-800/30 border-2 border-transparent hover:border-indigo-200/50 dark:hover:border-indigo-800/50'
                        }`}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <input
                          type="radio"
                          name="scheduleType"
                          value={option.value}
                          checked={isSelected}
                          onChange={(e) => handleInputChange('scheduleType', e.target.value as any)}
                          className="sr-only"
                        />
                        <motion.div
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            isSelected 
                              ? 'border-indigo-600 dark:border-indigo-400 bg-indigo-600 dark:bg-indigo-400' 
                              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                          }`}
                          animate={{
                            scale: isSelected ? [1, 1.2, 1] : 1,
                          }}
                          transition={{ duration: 0.3 }}
                        >
                          {isSelected && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                              className="w-2 h-2 bg-white rounded-full"
                            />
                          )}
                        </motion.div>
                        <span className={`text-sm font-medium ${isSelected ? 'text-slate-900 dark:text-white' : 'text-slate-700 dark:text-slate-300'}`}>
                          {option.label}
                        </span>
                      </motion.label>
                    );
                  })}
                </div>

                {formData.scheduleType === 'scheduled' && (
                  <motion.div 
                    className="grid grid-cols-2 gap-2"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <motion.input
                      type="date"
                      value={formData.scheduledDate}
                      onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
                      className="px-3 py-2 border-2 border-gray-200/50 dark:border-gray-700/50 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all text-sm"
                      whileFocus={{ scale: 1.02 }}
                    />
                    <motion.input
                      type="time"
                      value={formData.scheduledTime}
                      onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
                      className="px-3 py-2 border-2 border-gray-200/50 dark:border-gray-700/50 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-slate-700 dark:text-slate-300 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all text-sm"
                      whileFocus={{ scale: 1.02 }}
                    />
                  </motion.div>
                )}
              </div>
            </div>
          </div>
          </div>
        )}

        {/* Gönder Butonu - Her zaman göster */}
        <div className="flex justify-end">
          <motion.button
            type="submit"
            className="px-8 py-3 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white font-semibold rounded-xl shadow-xl flex items-center gap-2 relative overflow-hidden"
            whileHover={{ scale: 1.05, boxShadow: '0 20px 40px -10px rgba(99, 102, 241, 0.5)' }}
            whileTap={{ scale: 0.98 }}
          >
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
              animate={{
                x: ['-100%', '200%'],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'linear',
              }}
            />
            <span className="relative z-10 flex items-center gap-2">
              {renderIcon(FiSend, "w-5 h-5")}
              Gönder
            </span>
          </motion.button>
        </div>
      </form>

      {/* Geçmiş Bildirimler */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
        <h2 className="text-xl font-semibold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
          {renderIcon(FiBell, "w-5 h-5 text-indigo-600 dark:text-indigo-400")}
          Geçmiş Bildirimler
        </h2>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('date')}
                >
                  <div className="flex items-center gap-2">
                    <span>Tarih</span>
                    {sortColumn === 'date' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('title')}
                >
                  <div className="flex items-center gap-2">
                    <span>Başlık</span>
                    {sortColumn === 'title' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('note')}
                >
                  <div className="flex items-center gap-2">
                    <span>Not</span>
                    {sortColumn === 'note' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors select-none"
                  onClick={() => handleSort('sentCount')}
                >
                  <div className="flex items-center gap-2">
                    <span>Kaç Kişiye Gitti</span>
                    {sortColumn === 'sentCount' && renderIcon(sortDirection === 'asc' ? FiArrowUp : FiArrowDown, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
              {sortedNotifications.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                    Geçmiş bildirim bulunamadı
                  </td>
                </tr>
              ) : (
                sortedNotifications.map((notification) => (
                  <tr key={notification.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                      {new Date(notification.date).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-white">
                      {notification.title}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                      {notification.note}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                      {notification.sentCount.toLocaleString('tr-TR')}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;
